import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output
} from '@angular/core';
import {
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

import {
  AccountForSimplifiedPayment,
  OneTimePayFeatureFacade,
  PaymentOption
} from '@amfam/billing/one-time-pay/feature';
import { CurrencyUSDPipe } from '@amfam/shared/ui/pipes';
import { ErrorMessageService } from '@amfam/ui-kit';

@Component({
  selector: 'amfam-simplified-payment-checkbox',
  templateUrl: './simplified-payment-checkbox.component.html',
  styleUrls: ['./simplified-payment-checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SimplifiedPaymentCheckboxComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SimplifiedPaymentCheckboxComponent implements ControlValueAccessor, OnInit {
  @Input() billAccountOptions: AccountForSimplifiedPayment[];
  @Input() parentFormGroup: FormGroup;
  @Input() controlName: string;
  @Input() hasMultiBillAccForRome: boolean;
  @Input() romeFeaturePill: boolean;
  @Output() checkBoxClickEvent = new EventEmitter();
  @Output() linkTextEvent = new EventEmitter<{
    text: string;
    billAccountNumber: string;
  }>();
  @Output() updateAccountEvent = new EventEmitter<{
    billAccountOptions: AccountForSimplifiedPayment[];
    billAccountOption: AccountForSimplifiedPayment;
  }>();
  regex = /;/g;
  checkBoxOption: FormArray;

  constructor(private oneTimePayFacade: OneTimePayFeatureFacade) {}

  set value(val: unknown) {
    this.onChange(val);
    this.onTouched(val);
  }

  onChange: (value: unknown) => void = () => {};
  onTouched: (value: unknown) => void = () => {};

  ngOnInit(): void {
    this.checkBoxOption = this.parentFormGroup.get(this.controlName) as FormArray;
  }

  registerOnChange(fn: (value: unknown) => void) {
    this.onChange = fn;
  }

  writeValue(value: string) {
    this.value = value;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  checkBoxClick(option: FormControl, selectedOption: AccountForSimplifiedPayment) {
    if (!option.disabled) {
      option.setValue(!option.value);
      if (!option.value) {
        this.parentFormGroup.controls[selectedOption.id].disable();
        this.clearValidators(selectedOption);
      } else {
        this.parentFormGroup.controls[selectedOption.id].enable();
        if (
          this.parentFormGroup.controls[selectedOption.id].get('paymentOptions').value.id ===
          'otherAmount'
        ) {
          this.textBoxChangeEvent(
            null,
            this.parentFormGroup.controls[selectedOption.id].get('paymentOptions').value,
            selectedOption
          );
        }
      }
      this.checkBoxClickEvent.emit(Object.assign({}, selectedOption, { isSelected: option.value }));
    }
  }

  radioButtonClick(paymentOption: PaymentOption, option: AccountForSimplifiedPayment) {
    this.parentFormGroup.controls[option.id].get('paymentOptions').setValue(paymentOption);
    if (paymentOption.id !== 'otherAmount') {
      // clear radio field validations
      this.clearValidators(option);
      // remove less than mindue alert message
      if (option.alertMessage?.title === 'hasLessThanMinDue') {
        option.alertMessage.heading = null;
      }
    }
  }

  textBoxChangeEvent(
    event: FocusEvent | MouseEvent,
    paymentOption: PaymentOption,
    option: AccountForSimplifiedPayment
  ) {
    // do nothing for radio options except other amount field
    if (paymentOption.id === 'otherAmount') {
      if (event) {
        const element = event.target as HTMLInputElement;
        element.value = new CurrencyUSDPipe().transform(element.value.replace(/\$|,/g, ''));
        paymentOption.amount = element.value;
        this.parentFormGroup.controls[option.id].get('paymentOptions').setValue(paymentOption);
      }
      const otherPaymentAmountControl = this.parentFormGroup.controls[option.id].get(
        'otherAmount'
      ) as FormControl;
      otherPaymentAmountControl.setValue(paymentOption.amount);
      this.oneTimePayFacade.setOtherAmountValidator(option, this.parentFormGroup);
      otherPaymentAmountControl.markAsDirty();
      otherPaymentAmountControl.updateValueAndValidity();
      if (otherPaymentAmountControl.errors) {
        for (const propertyName in otherPaymentAmountControl.errors) {
          if (
            Object.prototype.hasOwnProperty.call(otherPaymentAmountControl.errors, propertyName)
          ) {
            option.radioFieldErrorMessage =
              ErrorMessageService.getValidatorErrorMessage(propertyName);
          }
        }
      }
      option.selectedPaymentAmount = paymentOption.amount;
      this.updateAccountEvent.emit({
        billAccountOptions: this.billAccountOptions,
        billAccountOption: option
      });
    }
  }

  textBoxClick(event: MouseEvent) {
    const element = event.target as HTMLInputElement;
    element.value = element.value === '$0.00' ? '$' : element.value;
  }

  linkEvent(text: string, billAccountNumber: string) {
    const linkEventObj = {
      text: text,
      billAccountNumber: billAccountNumber
    };
    this.linkTextEvent.emit(linkEventObj);
  }

  clearValidators(option: AccountForSimplifiedPayment) {
    const otherPaymentAmountControl = this.parentFormGroup.controls[option.id].get(
      'otherAmount'
    ) as FormControl;
    otherPaymentAmountControl?.clearValidators();
    otherPaymentAmountControl?.updateValueAndValidity();
  }
}
